import { useEffect, useCallback } from 'react';
import { createDefaultShape } from '../utils/createDefaultShape';
const NUDGE_AMOUNT = 1;
const SHIFT_NUDGE_AMOUNT = 10;
const RESIZE_AMOUNT = 0.02; // 2 % of canvas per Ctrl+Shift+Arrow press
const ROTATE_AMOUNT = 5; // degrees per [/] press
const ROTATE_FINE_AMOUNT = 1; // degrees per Shift+[/]
const TOOL_HOTKEYS = {
    b: 'box',
    o: 'circle',
    l: 'line',
    a: 'arrow',
    f: 'freeform',
    t: 'text',
    e: 'eraser',
};
export function useAnnotationKeyboard({ dispatch, selectedIds, shapes, activeTool, activeColor, zoom, nudge, containerRef, canvasRef, setStatus, startTextEdit, }) {
    const announce = useCallback((text) => {
        setStatus?.(text);
    }, [setStatus]);
    const isCanvasFocused = useCallback(() => {
        return !!canvasRef?.current && document.activeElement === canvasRef.current;
    }, [canvasRef]);
    const handleKeyDown = useCallback((e) => {
        // Ignore if typing in an input or contentEditable element.
        // Use composedPath()[0] to see through Shadow DOM boundaries (e.g. <comment-textarea>).
        const target = e.target;
        const actualTarget = (e.composedPath()[0] ?? target);
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' ||
            target.isContentEditable || actualTarget.isContentEditable) {
            return;
        }
        const key = e.key.toLowerCase();
        // Delete/Backspace — remove selected shape(s)
        if (key === 'delete' || key === 'backspace') {
            if (selectedIds.size > 0) {
                e.preventDefault();
                e.stopPropagation();
                dispatch({ type: 'DELETE_SELECTED' });
                announce(selectedIds.size === 1 ? 'Shape deleted' : `${selectedIds.size} shapes deleted`);
                return;
            }
        }
        // Undo: Ctrl+Z / Cmd+Z
        if (key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            dispatch({ type: 'UNDO' });
            return;
        }
        // Redo: Ctrl+Y / Cmd+Shift+Z
        if ((key === 'y' && (e.ctrlKey || e.metaKey)) ||
            (key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            dispatch({ type: 'REDO' });
            return;
        }
        // Tab / Shift+Tab is handled natively — each shape `<g>` is its own
        // tab stop in the editor, so the browser's default Tab behaviour
        // walks through them in DOM order. Selection follows DOM focus via
        // the SvgCanvas onShapeFocus handler.
        // Enter / Space on the canvas — insert a shape, or start text-edit if a
        // text shape is selected.
        if ((key === 'enter' || key === ' ') && isCanvasFocused()) {
            // If exactly one text shape is selected, enter text-edit mode
            if (selectedIds.size === 1) {
                const id = Array.from(selectedIds)[0];
                const shape = shapes.find((s) => s.id === id);
                if (shape && shape.type === 'text' && startTextEdit) {
                    e.preventDefault();
                    e.stopPropagation();
                    startTextEdit(id);
                    announce('Editing text');
                    return;
                }
            }
            // Otherwise insert a default-sized shape of the active tool
            const newShape = createDefaultShape(activeTool, activeColor, 0.5, 0.5, zoom);
            if (newShape) {
                e.preventDefault();
                e.stopPropagation();
                dispatch({ type: 'INSERT_SHAPE', shape: newShape });
                announce(`${activeTool} created`);
                return;
            }
        }
        // [ / ] — rotate selected
        if ((key === '[' || key === ']') && selectedIds.size > 0) {
            e.preventDefault();
            e.stopPropagation();
            const magnitude = e.shiftKey ? ROTATE_FINE_AMOUNT : ROTATE_AMOUNT;
            const delta = key === '[' ? -magnitude : magnitude;
            dispatch({ type: 'ROTATE_SELECTED', delta });
            announce(`Rotated ${delta > 0 ? 'right' : 'left'} ${Math.abs(delta)} degrees`);
            return;
        }
        // Arrow keys — nudge selected; Ctrl+Shift+Arrow resizes.
        if (key === 'arrowup' || key === 'arrowdown' || key === 'arrowleft' || key === 'arrowright') {
            if (selectedIds.size > 0) {
                e.preventDefault();
                e.stopImmediatePropagation();
                const isResize = (e.ctrlKey || e.metaKey) && e.shiftKey;
                if (isResize) {
                    let dw = 0, dh = 0;
                    switch (key) {
                        case 'arrowright':
                            dw = RESIZE_AMOUNT;
                            break;
                        case 'arrowleft':
                            dw = -RESIZE_AMOUNT;
                            break;
                        case 'arrowdown':
                            dh = RESIZE_AMOUNT;
                            break;
                        case 'arrowup':
                            dh = -RESIZE_AMOUNT;
                            break;
                    }
                    dispatch({ type: 'RESIZE_SELECTED', dw, dh });
                    announce(dw > 0 || dh > 0 ? 'Enlarged' : 'Shrunk');
                }
                else {
                    const amount = e.shiftKey ? SHIFT_NUDGE_AMOUNT : NUDGE_AMOUNT;
                    switch (key) {
                        case 'arrowup':
                            nudge(0, -amount);
                            break;
                        case 'arrowdown':
                            nudge(0, amount);
                            break;
                        case 'arrowleft':
                            nudge(-amount, 0);
                            break;
                        case 'arrowright':
                            nudge(amount, 0);
                            break;
                    }
                }
                return;
            }
        }
        // Escape — always deselect; if no selection and canvas focused, blur.
        if (key === 'escape') {
            e.preventDefault();
            e.stopPropagation();
            if (selectedIds.size > 0) {
                dispatch({ type: 'DESELECT_ALL' });
                announce('Selection cleared');
            }
            else if (isCanvasFocused()) {
                document.activeElement?.blur();
            }
            return;
        }
        // Tool hotkeys (only when no modifier keys)
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            const tool = TOOL_HOTKEYS[key];
            if (tool) {
                e.preventDefault();
                e.stopPropagation();
                dispatch({ type: 'SET_TOOL', tool });
                return;
            }
        }
    }, [dispatch, selectedIds, shapes, activeTool, activeColor, zoom, nudge, isCanvasFocused, announce, startTextEdit]);
    useEffect(() => {
        // Use the capture phase so this handler runs before any bubbling
        // document-level listeners (e.g. the player's arrow-key seek
        // shortcuts). preventDefault + stopImmediatePropagation in our
        // handler then prevents the player from consuming the event.
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true });
        };
    }, [handleKeyDown]);
}
